import { buildPageHeader } from "./sheet-ui.js";
/**
 * voice-v8 — Anwendungsbeispiele-View (Pre.347 Redesign).
 *
 * Edle Modul-Vitrine mit SVG-Icons (keine Emojis), Desktop-Vollbreite,
 * pro Modul klare "Was-tut-es / Was-braucht-es"-Erklärung.
 *
 * Liest Module-Status aus localStorage (gesetzt vom Agent via
 * voice-v8.modules data-packet). Wenn unbekannt → Cards bleiben aktiv
 * angezeigt (nicht ausgegraut) — Status wird beim ersten Anruf
 * verifiziert.
 */

interface ModulesInfo {
  active: string[];
  inactive: Array<{ name: string; reason: string }>;
  truthGuard?: boolean;
  receivedAt?: number;
}

interface ExampleSpec {
  /** Pflicht-Tools die aktiv sein müssen (mind. eines) */
  tools: string[];
  /** SVG-Path (Material-Style 24x24 viewBox, fill currentColor) */
  iconPath: string;
  title: string;
  /** 1-Satz was es macht */
  description: string;
  /** Beispiel-Prompt für den Chat */
  prompt: string;
  /*
   * ⚠️⚠️ 11.09.2026 — `requires` IST WEG, und zwar ersatzlos.
   *
   * Hier stand je Eintrag eine technische Voraussetzung, und sie stand auch
   * auf der Seite: „Voraussetzung: GOOGLE_API_KEY am Server", „OPENAI_API_KEY
   * am Server", „Nur Pipeline-Modus (im Realtime ist das Tool-Schema zu
   * gross)". Entwicklertext auf einer Nutzerseite — und zum Teil veraltet:
   * den Realtime-Modus in dieser Form gibt es nicht mehr.
   *
   * Martin: *„Die Anwendungsbeispiele stark vereinfachen … und bitte auch
   * entsprechend fuer die anderen User dann die Modelle nur anzeigen, die
   * auch wirklich freigegeben sind."* Auf die Frage, was mit der technischen
   * Zeile geschehen soll: *„Ganz weg fuer alle."*
   *
   * ⚠️ Die Regel dahinter: Was nicht geht, wird gar nicht erst gezeigt. Eine
   *    Karte, die erklaert, warum sie nicht funktioniert, ist eine
   *    Entschuldigung — und Entschuldigungen gehoeren nicht auf eine Seite,
   *    die zeigen soll, was moeglich ist.
   */
}

interface CategorySpec {
  iconPath: string;
  label: string;
  examples: ExampleSpec[];
}

// Inline SVG-Icons (Material-Style, viewBox 24×24, currentColor)
const ICONS = {
  brain:
    "M13 3a4 4 0 0 0-4 4v.5A3.5 3.5 0 0 0 5.5 11 3.5 3.5 0 0 0 7 17.86V20a2 2 0 0 0 4 0v-7h2v7a2 2 0 0 0 4 0v-2.14A3.5 3.5 0 0 0 18.5 11 3.5 3.5 0 0 0 15 7.5V7a4 4 0 0 0-2-4z",
  train:
    "M12 2c-4 0-8 .5-8 4v9.5A3.5 3.5 0 0 0 7.5 19L6 20.5V21h12v-.5L16.5 19a3.5 3.5 0 0 0 3.5-3.5V6c0-3.5-3.58-4-8-4zM7.5 17a1.5 1.5 0 1 1 0-3 1.5 1.5 0 0 1 0 3zm3.5-7H6V6h5v4zm2 0V6h5v4h-5zm3.5 7a1.5 1.5 0 1 1 0-3 1.5 1.5 0 0 1 0 3z",
  map: "M20.5 3l-.16.03L15 5.1 9 3 3.36 4.9c-.21.07-.36.25-.36.48V20.5c0 .28.22.5.5.5l.16-.03L9 18.9l6 2.1 5.64-1.9c.21-.07.36-.25.36-.48V3.5c0-.28-.22-.5-.5-.5zM15 19l-6-2.11V5l6 2.11V19z",
  mail: "M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z",
  draft:
    "M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04a1 1 0 0 0 0-1.41l-2.34-2.34a1 1 0 0 0-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z",
  calendar:
    "M19 3h-1V1h-2v2H8V1H6v2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11z",
  image:
    "M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z",
  brush:
    "M7 14c-1.66 0-3 1.34-3 3 0 1.31-1.16 2-2 2 .92 1.22 2.49 2 4 2 2.21 0 4-1.79 4-4 0-1.66-1.34-3-3-3zm13.71-9.37l-1.34-1.34a1 1 0 0 0-1.41 0L9 12.25 11.75 15l8.96-8.96a1 1 0 0 0 0-1.41z",
  fileDoc:
    "M14 2H6c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z",
  phone:
    "M6.62 10.79c1.44 2.83 3.76 5.15 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.32.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z",
  contacts:
    "M20 0H4v2h16V0zM4 24h16v-2H4v2zM20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm-8 2.75c1.24 0 2.25 1.01 2.25 2.25S13.24 11.25 12 11.25 9.75 10.24 9.75 9 10.76 6.75 12 6.75zM17 17H7v-1.5c0-1.67 3.33-2.5 5-2.5s5 .83 5 2.5V17z",
  music:
    "M12 3v10.55c-.59-.34-1.27-.55-2-.55-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4V7h4V3h-6z",
  folder:
    "M10 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2h-8l-2-2z",
  bookmark: "M17 3H7c-1.1 0-1.99.9-1.99 2L5 21l7-3 7 3V5c0-1.1-.9-2-2-2z",
  save: "M17 3H5c-1.11 0-2 .9-2 2v14c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V7l-4-4zm-5 16c-1.66 0-3-1.34-3-3s1.34-3 3-3 3 1.34 3 3-1.34 3-3 3zm3-10H5V5h10v4z",
  search:
    "M15.5 14h-.79l-.28-.27a6.5 6.5 0 1 0-.7.7l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0A4.5 4.5 0 1 1 14 9.5 4.5 4.5 0 0 1 9.5 14z",
  // 11.09.2026: der kleine Winkel am Zeilenende — er sagt „hier geht etwas auf".
  chevron: "M9.29 6.71a1 1 0 0 0 0 1.41L13.17 12l-3.88 3.88a1 1 0 1 0 1.42 1.41l4.58-4.58a1 1 0 0 0 0-1.42l-4.58-4.58a1 1 0 0 0-1.42 0z",
  globe:
    "M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM18.92 8h-2.95a15.65 15.65 0 0 0-1.38-3.56A8.03 8.03 0 0 1 18.92 8zM12 4.04c.83 1.2 1.48 2.53 1.91 3.96h-3.82c.43-1.43 1.08-2.76 1.91-3.96zM4.26 14C4.1 13.36 4 12.69 4 12s.1-1.36.26-2h3.38c-.08.66-.14 1.32-.14 2s.06 1.34.14 2H4.26zm.82 2h2.95c.32 1.25.78 2.45 1.38 3.56A7.987 7.987 0 0 1 5.08 16zm2.95-8H5.08a7.987 7.987 0 0 1 4.33-3.56C8.81 5.55 8.35 6.75 8.03 8zM12 19.96c-.83-1.2-1.48-2.53-1.91-3.96h3.82c-.43 1.43-1.08 2.76-1.91 3.96zM14.34 14H9.66c-.09-.66-.16-1.32-.16-2s.07-1.34.16-2h4.68c.09.66.16 1.32.16 2s-.07 1.34-.16 2zm.25 5.56c.6-1.11 1.06-2.31 1.38-3.56h2.95a8.03 8.03 0 0 1-4.33 3.56zM16.36 14c.08-.66.14-1.32.14-2s-.06-1.34-.14-2h3.38c.16.64.26 1.31.26 2s-.1 1.36-.26 2h-3.38z",
  timer:
    "M15 1H9v2h6V1zm-4 13h2V8h-2v6zm8.03-6.61l1.42-1.42c-.43-.51-.9-.99-1.41-1.41l-1.42 1.42A8.962 8.962 0 0 0 12 4c-4.97 0-9 4.03-9 9s4.02 9 9 9a8.994 8.994 0 0 0 7.03-14.61zM12 20c-3.87 0-7-3.13-7-7s3.13-7 7-7 7 3.13 7 7-3.13 7-7 7z",
  video:
    "M17 10.5V7c0-.55-.45-1-1-1H4c-.55 0-1 .45-1 1v10c0 .55.45 1 1 1h12c.55 0 1-.45 1-1v-3.5l4 4v-11l-4 4z",
  archive:
    "M20.54 5.23l-1.39-1.68A1.45 1.45 0 0 0 18 3H6c-.47 0-.88.21-1.16.55L3.46 5.23C3.17 5.57 3 6.02 3 6.5V19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V6.5c0-.48-.17-.93-.46-1.27zM12 17.5L6.5 12H10v-2h4v2h3.5L12 17.5zM5.12 5l.81-1h12l.94 1H5.12z",
  bot: "M20 9V7c0-1.1-.9-2-2-2h-3c0-1.66-1.34-3-3-3S9 3.34 9 5H6c-1.1 0-2 .9-2 2v2c-1.66 0-3 1.34-3 3s1.34 3 3 3v4c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2v-4c1.66 0 3-1.34 3-3s-1.34-3-3-3zM7.5 11.5A1.5 1.5 0 0 1 9 13a1.5 1.5 0 0 1-1.5 1.5A1.5 1.5 0 0 1 6 13a1.5 1.5 0 0 1 1.5-1.5zm9 0A1.5 1.5 0 0 1 18 13a1.5 1.5 0 0 1-1.5 1.5A1.5 1.5 0 0 1 15 13a1.5 1.5 0 0 1 1.5-1.5z",
  flight:
    "M21 16v-2l-8-5V3.5c0-.83-.67-1.5-1.5-1.5S10 2.67 10 3.5V9l-8 5v2l8-2.5V19l-2 1.5V21l3.5-1 3.5 1v-1.5L13 19v-5.5l8 2.5z",
};

const CATEGORIES: CategorySpec[] = [
  {
    iconPath: ICONS.brain,
    label: "Komplexe Aufgaben — Claude CLI",
    examples: [
      {
        tools: ["claude_task_server", "task_delegate"],
        iconPath: ICONS.fileDoc,
        title: "Buch oder langer Text schreiben",
        description:
          "Claude erstellt ein Kapitel, behält den Kontext für Folge-Anpassungen im selben Projekt.",
        prompt:
          'Schreib mir das erste Kapitel meines Buchs zum Thema "Zirbeldrüse aktivieren" — etwa 800 Wörter, locker erzählend.',
      },
      {
        tools: ["claude_task_server", "task_delegate"],
        iconPath: ICONS.brush,
        title: "Code generieren oder refactoren",
        description:
          "Bash, Python, JavaScript, TypeScript. Files werden im Projekt-Ordner abgelegt und überleben zwischen Aufrufen.",
        prompt:
          "Schreib mir ein Bash-Script, das alle PDFs in einem Ordner zu Markdown konvertiert.",
      },
      {
        tools: ["claude_task_server", "task_delegate"],
        iconPath: ICONS.search,
        title: "Tiefe Recherche mit Synthese",
        description: "Multi-Step-Recherche, ggf. mit Web-Suche und Quellen.",
        prompt:
          "Recherchier die drei wichtigsten Trends im Survival-Training 2026 mit Quellen.",
      },
    ],
  },
  {
    iconPath: ICONS.train,
    label: "Routen & Verkehrsverbindungen",
    examples: [
      {
        tools: ["oebb_connections"],
        iconPath: ICONS.train,
        title: "Zugverbindung suchen (ÖBB)",
        description:
          "Live-Abfahrten, Ankünfte und Umstiege direkt von oebb.at.",
        prompt: "Wann fährt der nächste Zug von Wien nach Salzburg?",
      },
      {
        tools: ["route_directions"],
        iconPath: ICONS.map,
        title: "Auto-Route mit Distanz und Dauer",
        description:
          "Google-Maps-Direktionen. Startpunkt kommt automatisch aus deinem Profil, wenn dort eine Adresse gespeichert ist.",
        prompt: "Wie lange fahre ich von zu Hause nach Linz?",
      },
      {
        tools: ["flight_search"],
        iconPath: ICONS.flight,
        title: "Flug suchen",
        description:
          "Direkt-Suche bei Duffel — vergleicht Iberia, British Airways, Air Baltic & andere. Preise in Echtzeit, mit Stops, Abflugs- und Ankunftszeiten.",
        prompt:
          "Such mir einen günstigen Flug von Wien nach Riga am 26. Mai, eine Person, Economy.",
      },
    ],
  },
  {
    iconPath: ICONS.mail,
    label: "Gmail & Kalender",
    examples: [
      {
        tools: ["gmail_draft_create"],
        iconPath: ICONS.draft,
        title: "Mail als Entwurf speichern (NICHT senden)",
        description:
          "Mail landet im Gmail-Drafts-Ordner. Du kannst sie in Gmail prüfen oder per Voice-Befehl freigeben.",
        prompt:
          "Bereite mir eine Mail an mmollay@gmail.com vor — Betreff Test, Inhalt 'Das ist ein Test'. Als Entwurf speichern, NICHT senden.",
      },
      {
        tools: ["gmail_send"],
        iconPath: ICONS.mail,
        title: "Mail direkt versenden",
        description:
          "Sendet sofort. Bot fragt vorher um Bestätigung — sage 'Schick ab' damit es rausgeht.",
        prompt:
          "Schreib eine Mail an X mit Betreff Y und Inhalt Z, zeig mir vorher den Entwurf zur Bestätigung.",
      },
      {
        tools: ["gmail_search", "gmail_read_message"],
        iconPath: ICONS.search,
        title: "Mails durchsuchen und vorlesen",
        description:
          "Gmail-Posteingang nach Betreff, Absender oder Zeit filtern. Volle Inhalte werden vom Bot vorgelesen.",
        prompt:
          'Such mir Mails von der letzten Woche zu "Rechnung" und lies mir die neueste komplett vor.',
      },
      {
        tools: ["calendar_list"],
        iconPath: ICONS.calendar,
        title: "Kalender-Termine anzeigen",
        description: "Was steht heute, morgen oder diese Woche an?",
        prompt: "Was sind meine Termine morgen?",
      },
      {
        tools: ["calendar_create"],
        iconPath: ICONS.bookmark,
        title: "Termin erstellen",
        description:
          "Bot legt einen Eintrag in Google Calendar an — mit Datum, Zeit, Ort.",
        prompt:
          "Trag mir morgen 14 Uhr einen Termin 'Zahnarzt Dr. Müller' ein.",
      },
    ],
  },
  {
    iconPath: ICONS.image,
    label: "Bild- und PDF-Generierung",
    examples: [
      {
        tools: ["generate_image"],
        iconPath: ICONS.image,
        title: "Bild generieren",
        description:
          "Gemini Imagen 4.0 — fotorealistisch, illustrativ oder stilisiert. Landet in der Mediathek.",
        prompt:
          "Generiere mir ein Bild: minimaler Bär, sage green, Aquarell-Stil, weißer Hintergrund.",
      },
      {
        tools: ["edit_image"],
        iconPath: ICONS.brush,
        title: "Bestehendes Bild bearbeiten",
        description:
          "Stil ändern, Hintergrund tauschen, Bildelemente anpassen — auf Basis eines vorhandenen Bildes.",
        prompt:
          "Mach mir das letzte Bild nochmal — diesmal als Strichzeichnung.",
      },
      {
        tools: ["make_pdf"],
        iconPath: ICONS.fileDoc,
        title: "PDF aus strukturiertem Text",
        description:
          "Markdown-Inhalt wird zu einer formatierten PDF — mit Titel, Abschnitten, optional als Mail-Anhang.",
        prompt:
          "Mach mir ein PDF mit dem Titel 'Konzept Workshop' und drei Abschnitten: Ziel, Ablauf, Materialliste.",
      },
    ],
  },
  {
    iconPath: ICONS.contacts,
    label: "Kontakte, Anrufe und Musik",
    examples: [
      {
        tools: ["contact_list"],
        iconPath: ICONS.contacts,
        title: "Kontakte zeigen",
        description:
          "Strukturierte Kontaktliste mit Name, Telefon, E-Mail, Adresse, Tags.",
        prompt: "Zeig mir meine Kontakte mit dem Tag 'Geschäft'.",
      },
      {
        tools: ["contact_call"],
        iconPath: ICONS.phone,
        title: "Anruf starten",
        description:
          "Wählt direkt aus der Kontaktliste über tel:- oder FaceTime-URL.",
        prompt: "Ruf bei Albert an.",
      },
      {
        tools: ["phone_play"],
        iconPath: ICONS.music,
        title: "Musik starten",
        description:
          "Deep-Link in Spotify oder Apple Music — öffnet die App auf dem Phone.",
        prompt: "Spiel mir Pink Floyd 'Comfortably Numb' auf Spotify ab.",
      },
    ],
  },
  {
    iconPath: ICONS.folder,
    label: "Projekte und Erinnerung",
    examples: [
      {
        tools: ["project_create"],
        iconPath: ICONS.folder,
        title: "Neues Projekt anlegen",
        description:
          "Eigener Arbeits-Scope mit File-Storage, Bot-Memory und CLI-Session.",
        prompt: "Leg mir ein neues Projekt 'Workshop März' an.",
      },
      {
        tools: ["project_file_create"],
        iconPath: ICONS.fileDoc,
        title: "Notiz im Projekt speichern",
        description:
          "Markdown-Datei im aktiven Projekt mit Embedding für semantische Suche.",
        prompt:
          "Speicher als Notiz im aktiven Projekt: 'Ideen-Liste für Workshop' und schreib 3 Ideen rein.",
      },
      {
        tools: ["memory_save"],
        iconPath: ICONS.save,
        title: "Wichtiges merken",
        description:
          "Persönliche Fakten oder Vorlieben werden dauerhaft im Bot-Memory hinterlegt.",
        prompt:
          "Merk dir: Ich trinke morgens immer einen Cappuccino mit Hafermilch.",
      },
      {
        tools: ["memory_search"],
        iconPath: ICONS.search,
        title: "Erinnerung durchsuchen",
        description:
          "Bot durchsucht alle gespeicherten Fakten, Vorlieben und Session-Zusammenfassungen.",
        prompt: "Was hatten wir letzte Woche zum Thema Buch besprochen?",
      },
    ],
  },
  {
    iconPath: ICONS.globe,
    label: "Web, Timer und Video-Analyse",
    examples: [
      {
        tools: ["web_search"],
        iconPath: ICONS.search,
        title: "Web durchsuchen",
        description:
          "Brave Search mit aktuellen Ergebnissen, kurzer Synthese der Top-Treffer.",
        prompt: "Such mir aktuelle Nachrichten zum Thema Bitcoin.",
      },
      {
        tools: ["timer_local"],
        iconPath: ICONS.timer,
        title: "Timer oder Countdown",
        description:
          "Lokaler Phone-Timer — vibriert und tönt nach Ablauf, läuft auch im Hintergrund.",
        prompt: "Stell mir einen Timer auf 10 Minuten für die Eier.",
      },
      {
        tools: ["analyze_video"],
        iconPath: ICONS.video,
        title: "YouTube-Video analysieren",
        description:
          "Gemini Vision plus Audio-Spur. Liefert Titel, Themen, Kernpunkte, Zitate. Kann direkt in ein Projekt gespeichert werden.",
        prompt:
          "Analysier mir dieses Video: https://youtube.com/watch?v=... und speicher die Zusammenfassung in Projekt Zirbeldrüse.",
      },
      {
        tools: ["live_translate"],
        iconPath: ICONS.globe,
        title: "Live-Übersetzung",
        description:
          "Übersetzt Wörter oder Sätze in 70+ Sprachen. Schnell, präzise, kein Tippen — der Bot liest die Übersetzung gleich vor.",
        prompt:
          "Übersetze 'Wo ist die nächste Apotheke?' ins Spanische und Italienische.",
      },
      {
        tools: ["browser_open_url"],
        iconPath: ICONS.video,
        title: "YouTube-Video starten",
        description:
          "Bot öffnet einen Song oder ein Video direkt im Chrome-Tab — ideal für Musik, Tutorials, schnelles Nachschlagen.",
        prompt:
          "Starte auf YouTube den Song 'Comfortably Numb' von Pink Floyd.",
      },
    ],
  },
];

function loadModules(): ModulesInfo | null {
  try {
    const raw = localStorage.getItem("voice-v8.modules");
    if (!raw) return null;
    return JSON.parse(raw) as ModulesInfo;
  } catch {
    return null;
  }
}

function moduleStatus(
  modules: ModulesInfo | null,
  toolNames: string[],
): { status: "active" | "inactive" | "unknown"; reason: string | null } {
  if (!modules) return { status: "unknown", reason: null };
  for (const t of toolNames) {
    if (modules.active.includes(t)) return { status: "active", reason: null };
  }
  for (const t of toolNames) {
    const hit = modules.inactive.find((i) => i.name === t);
    if (hit) return { status: "inactive", reason: hit.reason };
  }
  // Pre.348: Wenn das Tool WEDER in active NOCH in inactive erscheint,
  // ist die modules-Liste veraltet (neuer Server-Build hat das Tool noch
  // nicht propagiert). NICHT als "inactive" rendern — sonst zeigen wir
  // dem User "Modul nicht geladen" obwohl es vielleicht längst läuft
  // (z.B. gmail_draft_create funktioniert live, taucht aber im alten
  // Modules-Snapshot nicht auf). → "unknown" damit Card normal nutzbar.
  return { status: "unknown", reason: null };
}

function makeIcon(path: string, size = 20): SVGSVGElement {
  const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
  svg.setAttribute("viewBox", "0 0 24 24");
  svg.setAttribute("width", String(size));
  svg.setAttribute("height", String(size));
  svg.setAttribute("aria-hidden", "true");
  svg.setAttribute("focusable", "false");
  const p = document.createElementNS("http://www.w3.org/2000/svg", "path");
  p.setAttribute("d", path);
  p.setAttribute("fill", "currentColor");
  svg.appendChild(p);
  return svg;
}

export class ExamplesView {
  private root: HTMLElement;

  constructor(root: HTMLElement) {
    this.root = root;
    this.render();
    window.addEventListener("voice-v8:modules-updated", () => this.render());
  }

  /**
   * Ein Eintrag im Grossen — Beschreibung, Beispielsatz, ein Knopf.
   *
   * ⚠️ 11.09.2026: Vorher stand all das auf der Seite selbst, bei 28
   *    Eintraegen. Martin: *„Anwendungsbeispiele stark vereinfachen. Entweder
   *    mit Fallbeispielen oben als kurze Uebersicht, wo ich dann draufklicken
   *    kann, wo man dann beispielsweise ein Modal bekommt fuer mehr
   *    Information."*
   */
  private zeigeEintrag(ex: ExampleSpec): void {
    const hinter = document.createElement("div");
    hinter.className = "beispiel-hinter";
    const karte = document.createElement("div");
    karte.className = "beispiel-modal";
    karte.setAttribute("role", "dialog");
    karte.setAttribute("aria-modal", "true");
    karte.setAttribute("aria-label", ex.title);

    const kopf = document.createElement("div");
    kopf.className = "beispiel-modal-kopf";
    const symbol = document.createElement("span");
    symbol.className = "beispiel-modal-symbol";
    symbol.appendChild(makeIcon(ex.iconPath, 20));
    const titel = document.createElement("h3");
    titel.className = "beispiel-modal-titel";
    titel.textContent = ex.title;
    kopf.append(symbol, titel);

    const text = document.createElement("p");
    text.className = "beispiel-modal-text";
    text.textContent = ex.description;

    const satz = document.createElement("p");
    satz.className = "beispiel-modal-satz";
    satz.textContent = ex.prompt;

    const reihe = document.createElement("div");
    reihe.className = "beispiel-modal-reihe";
    const nehmen = document.createElement("button");
    nehmen.type = "button";
    nehmen.className = "beispiel-modal-knopf beispiel-modal-knopf--haupt";
    nehmen.textContent = "Satz übernehmen";
    const zu = document.createElement("button");
    zu.type = "button";
    zu.className = "beispiel-modal-knopf";
    zu.textContent = "Schließen";
    reihe.append(zu, nehmen);

    karte.append(kopf, text, satz, reihe);
    hinter.appendChild(karte);

    const schliessen = (): void => {
      hinter.remove();
      document.removeEventListener("keydown", aufTaste);
    };
    /*
     * ⚠️ Escape und ein Tipp daneben schliessen. Ein Modal ohne beides ist
     *    auf dem Telefon eine Sackgasse — man sieht den Schliessen-Knopf
     *    nicht, wenn die Tastatur oder ein langer Text ihn verdeckt.
     */
    const aufTaste = (e: KeyboardEvent): void => {
      if (e.key === "Escape") schliessen();
    };
    document.addEventListener("keydown", aufTaste);
    hinter.addEventListener("click", (e) => {
      if (e.target === hinter) schliessen();
    });
    zu.addEventListener("click", schliessen);
    nehmen.addEventListener("click", () => {
      window.dispatchEvent(
        new CustomEvent("voice-v8:example-prompt", {
          detail: { prompt: ex.prompt },
        }),
      );
      schliessen();
    });

    document.body.appendChild(hinter);
    nehmen.focus();
  }

  private render(): void {
    while (this.root.firstChild) this.root.removeChild(this.root.firstChild);
    /*
     * ⚠️⚠️ 11.09.2026 — DIE SEITE TRAEGT JETZT DIE SEITEN-NORM.
     *
     * Martin, mit Bild: *„Erstens passt der Header nicht."* Er hatte recht,
     * und der Grund war strukturell: Diese Seite war die EINZIGE, die nicht
     * `.page-sheet` trug. Damit bekam ihr Kopf nichts von dem, was alle
     * anderen haben — kein `position: sticky`, keinen eigenen Hintergrund,
     * keinen Bleed ueber die Polsterung (style.css, `.page-sheet .page-head`).
     * Er scrollte mit, und der Inhalt schob sich daran vorbei.
     *
     * ⚠️ NICHT `buildPageHeader` angefasst — den teilen sich sechzehn
     *    Ansichten. Falsch war die Huelle, nicht der Kopf.
     */
    this.root.classList.add("examples-view", "page-sheet");

    const modules = loadModules();

    const header = buildPageHeader("Anwendungsbeispiele", () => {}, {
      subtitle: "Was Vespera für dich tun kann. Tippe auf einen Eintrag.",
    });
    this.root.appendChild(header);

    const koerper = document.createElement("div");
    koerper.className = "page-body";
    this.root.appendChild(koerper);

    /*
     * ⚠️ Der Anruf ist seit pre.2841 nur fuer den Hauptnutzer (er ist der
     *    einzige Kanal mit echten Kosten). Eine Seite, die ihn trotzdem
     *    anpreist, waere ein Versprechen an eine verschlossene Tuer — genau
     *    das, was Martin an dieser Seite gestoert hat.
     */
    /*
     * ⚠️ `__voiceV8Uid` ist die Quelle, die das Haus schon benutzt (app.ts
     *    setzt sie nach /api/auth/me, settings-view liest sie ebenso). Mein
     *    erster Anlauf griff auf einen localStorage-Schluessel zu, den
     *    NIEMAND schreibt — er haette immer „Hauptnutzer" ergeben und die
     *    Sperre wirkungslos gemacht. Beim Nachsehen aufgefallen, nicht beim
     *    Testen: Ein Test auf einen Wert, den es nicht gibt, waere gruen
     *    geblieben.
     *
     * ⚠️ Fehlt die Zahl (Ein-Nutzer-Betrieb ohne Anmeldung), gilt
     *    „Hauptnutzer" — dieselbe Vorgabe wie am Server (DEFAULT_USER_ID=1).
     */
    const uid = (window as unknown as { __voiceV8Uid?: number }).__voiceV8Uid;
    const istHaupt = uid === undefined || uid === 1;

    let gezeigt = 0;
    for (const cat of CATEGORIES) {
      const section = document.createElement("section");
      section.className = "examples-category";

      const catHead = document.createElement("h3");
      catHead.className = "examples-category-title";
      catHead.appendChild(makeIcon(cat.iconPath, 18));
      const catLabel = document.createElement("span");
      catLabel.textContent = cat.label;
      catHead.appendChild(catLabel);

      const liste = document.createElement("div");
      liste.className = "beispiel-liste";

      for (const ex of cat.examples) {
        const st = moduleStatus(modules, ex.tools);
        // Was nicht geht, wird gar nicht erst gezeigt (Martin: „ganz weg
        // fuer alle"). „unknown" bleibt: der Modul-Stand kann fehlen, und
        // eine leere Seite waere schlechter als eine ungeprueste Zeile.
        if (st.status === "inactive") continue;
        if (!istHaupt && ex.tools.includes("contact_call")) continue;

        const zeile = document.createElement("button");
        zeile.type = "button";
        zeile.className = "beispiel-zeile";
        const symbol = document.createElement("span");
        symbol.className = "beispiel-zeile-symbol";
        symbol.appendChild(makeIcon(ex.iconPath, 18));
        const wort = document.createElement("span");
        wort.className = "beispiel-zeile-wort";
        wort.textContent = ex.title;
        const pfeil = document.createElement("span");
        pfeil.className = "beispiel-zeile-pfeil";
        pfeil.setAttribute("aria-hidden", "true");
        pfeil.appendChild(makeIcon(ICONS.chevron, 16));
        zeile.append(symbol, wort, pfeil);
        zeile.addEventListener("click", () => this.zeigeEintrag(ex));
        liste.appendChild(zeile);
        gezeigt++;
      }

      if (liste.childElementCount === 0) continue;
      section.append(catHead, liste);
      koerper.appendChild(section);
    }

    /*
     * ⚠️ Nicht still leer bleiben. Steht hier nichts, ist etwas kaputt — und
     *    eine leere Seite behauptet, Vespera koenne nichts.
     */
    if (gezeigt === 0) {
      const leer = document.createElement("p");
      leer.className = "examples-meta";
      leer.textContent =
        "Die Übersicht lässt sich gerade nicht laden. Sprich einfach los — es funktioniert trotzdem.";
      koerper.appendChild(leer);
    }
  }
}
